import React, { useState } from 'react';
import { bearerHeaders } from '../api.ts';
import { getTheme, useThemeMode } from '../theme';
import { resolveUrl } from '../api';
import type { QidUser, Mark } from '../api';
import type { WizardData } from '../types';
import { PubliceringsAvtalModal } from '../ApplicationSystem';
import { FastighetMiniMap } from './FastighetMiniMap';

const GAME_TYPE_OPTIONS = ['Älg', 'Dovhjort', 'Kronhjort', 'Rådjur', 'Vildsvin', 'Björn', 'Hare', 'Fasan', 'And', 'Kanadagås', 'Räv', 'Grävling'];
const FACILITY_OPTIONS = ['Slaktrum', 'Kylrum', 'Viltkamera', 'Eldstad/grill', 'Jaktkoja/stuga', 'Jakttorn', 'Åtelbod', 'Parkeringsplats', 'Eluttag'];
const DIFFICULTY_OPTIONS = ['Lätt', 'Medel', 'Avancerad', 'Expert'];

const WIZARD_STEPS = [
  { title: 'Grundinfo om marken', sub: 'Berätta var marken ligger och vad som jagas' },
  { title: 'Kontaktuppgifter', sub: 'Hur kontaktar vi dig och jägarna dig?' },
  { title: 'Beskrivning', sub: 'Locka jägare med en bra presentation' },
  { title: 'Ekonomi & betalning', sub: 'Hur vill du ta betalt och faktureras?' },
  { title: 'Granska & skicka in', sub: 'Kontrollera uppgifterna innan du skickar' },
];

export function MarkRegistrationWizard({ user, onClose, onComplete, initialData }: { user: QidUser | null; onClose: () => void; onComplete: (mark: Mark) => void; initialData?: Partial<WizardData> }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [success, setSuccess] = useState(false);
  const [showWizPubAvtal, setShowWizPubAvtal] = useState(false);
  const [wizPubSigned, setWizPubSigned] = useState(false);
  const [wizPubSignName, setWizPubSignName] = useState('');
  const [data, setData] = useState<WizardData>({
    fastighetsBeteckning: initialData?.fastighetsBeteckning || '', name: initialData?.name || '', areaHa: initialData?.areaHa || '',
    county: initialData?.county || '', municipality: initialData?.municipality || '',
    gameTypes: initialData?.gameTypes || [], difficulty: initialData?.difficulty || 'Medel',
    contactName: initialData?.contactName || user?.name || '', contactEmail: initialData?.contactEmail || user?.email || '',
    contactPhone: initialData?.contactPhone || '', address: initialData?.address || '',
    description: initialData?.description || '', facilities: initialData?.facilities || [], highlights: initialData?.highlights || '',
    ownerType: initialData?.ownerType || 'person', ownerNumber: initialData?.ownerNumber || '',
    swishNumber: initialData?.swishNumber || '', swishAmount: initialData?.swishAmount || '',
    invoiceEmail: initialData?.invoiceEmail || user?.email || '',
  });

  const upd = (k: keyof WizardData, v: string | string[]) => setData(p => ({ ...p, [k]: v }));
  const toggleArr = (k: 'gameTypes' | 'facilities', v: string) => upd(k, data[k].includes(v) ? data[k].filter(x => x !== v) : [...data[k], v]);

  const validate = (): string => {
    if (step === 0) { if (!data.name.trim()) return 'Markens namn krävs.'; }
    if (step === 1) { if (!data.contactEmail.trim() || !data.contactEmail.includes('@')) return 'Giltig e-postadress krävs.'; }
    if (step === 3) { if (data.invoiceEmail && !data.invoiceEmail.includes('@')) return 'Giltig faktura-epost krävs.'; }
    return '';
  };

  const next = () => { const e = validate(); if (e) { setErr(e); return; } setErr(''); setStep(s => Math.min(s + 1, 4)); };
  const back = () => { setErr(''); setStep(s => Math.max(s - 1, 0)); };

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json', 'X-Api-Key': 'dev-secret' };
      const t = localStorage.getItem('qvenari_access_token'); if (t) Object.assign(headers,bearerHeaders(t));
      const gqlBody = {
        query: `mutation RM($input:RegisterMarkInput!){registerMark(input:$input){id name county municipality areaHa contactEmail contactPhone contactName swishNumber swishAmount ownerType ownerNumber invoiceEmail registrationStatus isPublished fastighetsBeteckning gameTypesJson facilitiesJson highlightsJson difficulty description galleryUrls imageUrl mapLat mapLng pricePerYear createdAt updatedAt}}`,
        variables: {
          input: {
            name: data.name, fastighetsBeteckning: data.fastighetsBeteckning || undefined, areaHa: data.areaHa ? Number(data.areaHa) : undefined,
            county: data.county || undefined, municipality: data.municipality || undefined, address: data.address || undefined,
            gameTypesJson: JSON.stringify(data.gameTypes), difficulty: data.difficulty || undefined,
            contactName: data.contactName || undefined, contactEmail: data.contactEmail, contactPhone: data.contactPhone || undefined,
            description: data.description || undefined,
            facilitiesJson: JSON.stringify(data.facilities),
            highlightsJson: JSON.stringify(data.highlights ? data.highlights.split('\n').filter(Boolean) : []),
            ownerType: data.ownerType, ownerNumber: data.ownerNumber || undefined,
            swishNumber: data.swishNumber || undefined, swishAmount: data.swishAmount ? Number(data.swishAmount) : undefined,
            invoiceEmail: data.invoiceEmail || undefined,
          }
        },
      };
      const res = await fetch(resolveUrl('/graphql'), { method: 'POST', headers, body: JSON.stringify(gqlBody) });
      const json = await res.json();
      if (json.errors?.length) throw new Error(json.errors[0].message);
      setSuccess(true);
      setTimeout(() => { try { onComplete(json.data.registerMark); } catch (ex) { console.error('onComplete crash', ex); } }, 1800);
    } catch (e: any) { setErr(e.message || 'Något gick fel'); }
    finally { setBusy(false); }
  };

  const iS = { background: T.bg, border: `1px solid ${T.border}`, borderRadius: 5, padding: '10px 14px', color: T.text, fontSize: 14, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' as const };
  const lS = { color: T.textMuted, fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' as const };
  const chipStyle = (active: boolean) => ({ display: 'inline-flex', alignItems: 'center', padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: `1px solid ${active ? T.amber : T.border}`, background: active ? T.amber : 'transparent', color: active ? '#000' : T.textSub, transition: 'all 0.15s', userSelect: 'none' as const });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', overflowY: 'auto' }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', padding: '0 0 24px' }}>

        {/* Header */}
        <div style={{ background: T.surface, borderRadius: '10px 10px 0 0', padding: '20px 24px', borderBottom: `1px solid ${T.border}`, position: 'sticky', top: 0, zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ color: T.amber, fontSize: 18, fontWeight: 800 }}>Registrera din mark</div>
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: T.textMuted, fontSize: 22, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>×</button>
          </div>
          {/* Progress bar */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
            {WIZARD_STEPS.map((_, i) => (
              <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= step ? T.amber : T.border, transition: 'background 0.3s' }} />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div style={{ color: T.text, fontSize: 15, fontWeight: 700 }}>{WIZARD_STEPS[step].title}</div>
            <div style={{ color: T.textMuted, fontSize: 12 }}>Steg {step + 1} av {WIZARD_STEPS.length}</div>
          </div>
          <div style={{ color: T.textSub, fontSize: 13, marginTop: 2 }}>{WIZARD_STEPS[step].sub}</div>
        </div>

        {/* Body */}
        <div style={{ padding: '24px 24px 0' }}>

          {success && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
              <div style={{ color: T.amber, fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Marken är inskickad!</div>
              <div style={{ color: T.textSub, fontSize: 15 }}>En av våra jaktansvariga hör av sig till dig inom kort för att skriva kontrakt och publicera marken.</div>
            </div>
          )}

          {!success && step === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {/* Fastighetsbeteckning med live-kartpreview */}
              <div>
                <label>
                  <span style={lS}>Fastighetsbeteckning</span>
                  <input style={iS} value={data.fastighetsBeteckning} onChange={e => upd('fastighetsBeteckning', e.target.value)} placeholder="T.ex. Falun Rökärr 1:1" />
                </label>
                <div style={{ color: T.textMuted, fontSize: 11, marginTop: 3 }}>Ange beteckning så visas fastigheten på karta och kommun fylls i automatiskt.</div>
                <FastighetMiniMap
                  beteckning={data.fastighetsBeteckning}
                  onAutoFill={fields => setData(p => ({
                    ...p,
                    municipality: p.municipality || fields.municipality || p.municipality,
                    county: p.county || fields.county || p.county,
                  }))}
                />
              </div>
              <label><span style={lS}>Markens namn *</span><input style={iS} value={data.name} onChange={e => upd('name', e.target.value)} placeholder="T.ex. Rökärrs Jaktmark" /></label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label><span style={lS}>Areal (hektar)</span><input style={iS} type="number" min={0} value={data.areaHa} onChange={e => upd('areaHa', e.target.value)} placeholder="T.ex. 350" /></label>
                <label><span style={lS}>Svårighetsgrad</span>
                  <select style={iS} value={data.difficulty} onChange={e => upd('difficulty', e.target.value)}>
                    {DIFFICULTY_OPTIONS.map(d => <option key={d}>{d}</option>)}
                  </select>
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label><span style={lS}>Län</span><input style={iS} value={data.county} onChange={e => upd('county', e.target.value)} placeholder="T.ex. Dalarna" /></label>
                <label><span style={lS}>Kommun</span><input style={iS} value={data.municipality} onChange={e => upd('municipality', e.target.value)} placeholder="T.ex. Falun" /></label>
              </div>
              <div>
                <span style={lS}>Viltslag på marken</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                  {GAME_TYPE_OPTIONS.map(g => (
                    <span key={g} onClick={() => toggleArr('gameTypes', g)} style={chipStyle(data.gameTypes.includes(g))}>{g}</span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!success && step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <label><span style={lS}>Ditt namn</span><input style={iS} value={data.contactName} onChange={e => upd('contactName', e.target.value)} placeholder="För- och efternamn" /></label>
              <label><span style={lS}>E-post *</span><input style={iS} type="email" value={data.contactEmail} onChange={e => upd('contactEmail', e.target.value)} placeholder="din@email.se" /></label>
              <label><span style={lS}>Telefon</span><input style={iS} type="tel" value={data.contactPhone} onChange={e => upd('contactPhone', e.target.value)} placeholder="070-123 45 67" /></label>
              <label><span style={lS}>Adress / Närmaste ort</span><input style={iS} value={data.address} onChange={e => upd('address', e.target.value)} placeholder="T.ex. Skinnarbyvägen 12, Falun" /></label>
            </div>
          )}

          {!success && step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <label>
                <span style={lS}>Beskrivning av marken</span>
                <textarea style={{ ...iS, minHeight: 120, resize: 'vertical' }} value={data.description} onChange={e => upd('description', e.target.value)} placeholder="Beskriv terrängen, jakttraditioner, vad som är unikt med din mark..." />
              </label>
              <div>
                <span style={lS}>Faciliteter</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                  {FACILITY_OPTIONS.map(f => (
                    <span key={f} onClick={() => toggleArr('facilities', f)} style={chipStyle(data.facilities.includes(f))}>{f}</span>
                  ))}
                </div>
              </div>
              <label>
                <span style={lS}>Höjdpunkter (en per rad)</span>
                <textarea style={{ ...iS, minHeight: 90, resize: 'vertical' }} value={data.highlights} onChange={e => upd('highlights', e.target.value)} placeholder={'T.ex.\nStor älgstam\nEget slaktrum\nBarnvänlig jakt'} />
              </label>
            </div>
          )}

          {!success && step === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div>
                <span style={lS}>Ägare av jakträtten</span>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  {(['person', 'foretag'] as const).map(ot => (
                    <button key={ot} onClick={() => upd('ownerType', ot)} style={{ flex: 1, background: data.ownerType === ot ? T.amber : T.bg, color: data.ownerType === ot ? '#000' : T.textMuted, border: `1px solid ${data.ownerType === ot ? T.amber : T.border}`, borderRadius: 6, padding: '10px 0', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                      {ot === 'person' ? 'Person' : 'Företag'}
                    </button>
                  ))}
                </div>
                <input style={{ ...iS, marginTop: 8 }} value={data.ownerNumber} onChange={e => upd('ownerNumber', e.target.value)} placeholder={data.ownerType === 'person' ? 'Personnummer (19800101-1234)' : 'Org.nr (556000-1234)'} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label><span style={lS}>Swish-nummer för utbetalning</span><input style={iS} value={data.swishNumber} onChange={e => upd('swishNumber', e.target.value)} placeholder="123 456 78 90" /></label>
                <label><span style={lS}>Belopp per jakt (kr)</span><input style={iS} type="number" min={0} value={data.swishAmount} onChange={e => upd('swishAmount', e.target.value)} placeholder="0" /></label>
              </div>
              <label>
                <span style={lS}>E-post för fakturaunderlag</span>
                <input style={iS} type="email" value={data.invoiceEmail} onChange={e => upd('invoiceEmail', e.target.value)} placeholder="faktura@dinmark.se" />
                <span style={{ color: T.textMuted, fontSize: 11, marginTop: 4, display: 'block' }}>Månadsvis fakturaunderlag skickas hit. Betalas inom 30 dagar.</span>
              </label>
            </div>
          )}

          {!success && step === 4 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                ...(data.fastighetsBeteckning ? [['Fastighetsbeteckning', data.fastighetsBeteckning]] : [] as string[][]),
                ['Markens namn', data.name],
                ['Areal', data.areaHa ? `${data.areaHa} ha` : '—'],
                ['Plats', `${data.municipality || '—'}${data.county ? ', ' + data.county : ''}`],
                ['Svårighetsgrad', data.difficulty],
                ['Viltslag', data.gameTypes.join(', ') || '—'],
                ['Kontakt', `${data.contactName || ''} <${data.contactEmail}>${data.contactPhone ? ' · ' + data.contactPhone : ''}`],
                ['Adress', data.address || '—'],
                ['Ägare', `${data.ownerType === 'person' ? 'Person' : 'Företag'}${data.ownerNumber ? ' · ' + data.ownerNumber : ''}`],
                ['Swish', data.swishNumber || '—'],
                ['Faktura-epost', data.invoiceEmail || '—'],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ color: T.textMuted, fontSize: 13, fontWeight: 600, minWidth: 140, flexShrink: 0 }}>{k}</div>
                  <div style={{ color: T.text, fontSize: 13 }}>{v}</div>
                </div>
              ))}
              {data.description && (
                <div style={{ padding: '10px 0', borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ color: T.textMuted, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Beskrivning</div>
                  <div style={{ color: T.text, fontSize: 13, lineHeight: 1.6 }}>{data.description}</div>
                </div>
              )}
              {/* Publiceringsavtal */}
              <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: '16px 18px', marginTop: 4 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 8 }}>Publiceringsavtal med venari</div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' as const, marginBottom: 12 }}>
                  <span style={{ fontSize: 12, color: T.textSub }}>Förmedlingsarvode: <strong style={{ color: '#EE8F09' }}>20 %</strong></span>
                  <span style={{ fontSize: 12, color: T.textSub }}>Din utbetalning: <strong style={{ color: '#61D836' }}>80 %</strong></span>
                  <span style={{ fontSize: 12, color: T.textSub }}>Betalningstid: <strong>30 dagar</strong></span>
                  <span style={{ fontSize: 12, color: T.textSub }}>Fakturaunderlag: <strong>månadsvis</strong></span>
                </div>
                <div style={{ fontSize: 12, color: T.textSub, marginBottom: 14, lineHeight: 1.6 }}>
                  Avtalet ger venari rätt att publicera din mark och förmedla jakträtter till jägare. Kostnadsfri publicering — venari ersätts enbart vid genomförda transaktioner.
                </div>
                {wizPubSigned
                  ? <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: '#4CAF50', fontWeight: 700, fontSize: 13 }}>✓ Signerat av {wizPubSignName}</span>
                    <button onClick={() => setShowWizPubAvtal(true)} style={{ background: 'none', border: `1px solid ${T.border}`, borderRadius: 5, padding: '5px 12px', color: T.textSub, cursor: 'pointer', fontSize: 12 }}>Visa avtal</button>
                  </div>
                  : <button onClick={() => setShowWizPubAvtal(true)} style={{ background: '#EE8F09', border: 'none', borderRadius: 6, padding: '9px 18px', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>Granska &amp; signera avtal</button>
                }
              </div>
              <div style={{ background: 'rgba(238,143,9,0.08)', border: `1px solid ${T.amberDim}`, borderRadius: 6, padding: '12px 16px', marginTop: 4 }}>
                <div style={{ color: T.amber, fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Vad händer nu?</div>
                <div style={{ color: T.textSub, fontSize: 13, lineHeight: 1.6 }}>Din mark publiceras direkt när du skickar in. venari skickar fakturaunderlag månadsvis för genomförda bokningar.</div>
              </div>
            </div>
          )}
          {/* Wizard publiceringsavtal modal */}
          {showWizPubAvtal && (
            <PubliceringsAvtalModal
              markName={data.name}
              ownerName={data.contactName || user?.name}
              ownerEmail={data.contactEmail || user?.email}
              orgNr={data.ownerNumber}
              areaHa={data.areaHa ? Number(data.areaHa) : undefined}
              onClose={() => setShowWizPubAvtal(false)}
              onSigned={(name) => { setWizPubSigned(true); setWizPubSignName(name); setShowWizPubAvtal(false); }}
            />
          )}

          {err && <div style={{ color: '#ff6b6b', background: 'rgba(255,100,100,0.1)', borderRadius: 5, padding: '10px 14px', fontSize: 13, marginTop: 16 }}>{err}</div>}
        </div>

        {/* Footer buttons */}
        {!success && (
          <div style={{ display: 'flex', gap: 10, padding: '24px 24px 0' }}>
            {step > 0 && <button onClick={back} style={{ flex: 1, background: 'transparent', color: T.textSub, border: `1px solid ${T.border}`, borderRadius: 6, padding: '13px 0', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>← Tillbaka</button>}
            {step < 4 && <button onClick={next} style={{ flex: 2, background: T.amber, color: '#000', border: 'none', borderRadius: 6, padding: '13px 0', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Nästa →</button>}
            {step === 4 && <button onClick={submit} disabled={busy} style={{ flex: 2, background: T.amber, color: '#000', border: 'none', borderRadius: 6, padding: '13px 0', fontSize: 15, fontWeight: 700, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit', opacity: busy ? 0.7 : 1 }}>{busy ? 'Skickar in...' : 'Skicka in marken'}</button>}
          </div>
        )}
      </div>
    </div>
  );
}
